import {
  AppErrorCode,
  type MediaKind,
  type ApiErrorDto,
  type AuthResponseDto,
  type ChatWithMessagesDto,
  type MessageDto,
  type RefreshResponseDto,
  type IssueContext,
  type FriendRequestDto,
  type SavedChatDto,
  type UserDto,
  type WebLoginStatusDto,
  type WebLoginTicketDto,
} from '@anon-chat/shared';

const API_URL = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly code: AppErrorCode,
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = 'anonchat.tokens';
const GUEST_KEY = 'anonchat.guestId';

function readTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function writeTokens(tokens: Tokens | null): void {
  try {
    if (tokens) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Приватный режим браузера: работаем в памяти текущей сессии.
  }
}

let tokens: Tokens | null = readTokens();
let refreshing: Promise<void> | null = null;

export function getAccessToken(): string | null {
  return tokens?.accessToken ?? null;
}

export function setTokens(next: Tokens | null): void {
  tokens = next;
  writeTokens(next);
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorDto;
    return new ApiError(body.code, body.message, response.status);
  } catch {
    return new ApiError(AppErrorCode.INTERNAL_ERROR, 'Ошибка сети', response.status);
  }
}

/**
 * Обновление токена выполняется один раз на все параллельные запросы: иначе
 * несколько 401 подряд отправили бы несколько refresh, а он одноразовый —
 * второй использовал бы уже отозванный токен и обнулил бы сессию.
 */
async function refreshTokens(): Promise<void> {
  if (refreshing) {
    return refreshing;
  }

  const current = tokens?.refreshToken;
  if (!current) {
    throw new ApiError(AppErrorCode.UNAUTHORIZED, 'Нет refresh-токена', 401);
  }

  refreshing = (async () => {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: current }),
    });

    if (!response.ok) {
      // Пара стирается, только если сервер отверг сам токен. Отказ из-за
      // недоступной зависимости — временный, и выбрасывать из учётной записи
      // из-за него нельзя: гость потеряет доступ к своим диалогам.
      if (response.status < 500) {
        setTokens(null);
      }
      throw await parseError(response);
    }

    const data = (await response.json()) as RefreshResponseDto;
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  })().finally(() => {
    refreshing = null;
  });

  return refreshing;
}

/**
 * Обновить пару токенов вне обычного запроса.
 *
 * Нужно сокету: его рукопожатие проходит мимо `request`, и протухший
 * access-токен там некому обновить. Возвращает `false`, если обновляться нечем
 * или сервер отверг refresh, — тогда нужен полноценный вход.
 */
export async function renewSession(): Promise<boolean> {
  if (!tokens?.refreshToken) {
    return false;
  }

  try {
    await refreshTokens();
    return true;
  } catch {
    return false;
  }
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; retry?: boolean } = {},
): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      'content-type': 'application/json',
      ...(tokens?.accessToken ? { authorization: `Bearer ${tokens.accessToken}` } : {}),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  if (response.status === 401 && options.retry !== false && tokens?.refreshToken) {
    await refreshTokens();
    return request<T>(path, { ...options, retry: false });
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  // Пустое тело — законный ответ: так выглядит «активного диалога нет».
  // Разбор его как JSON бросал исключение, и вызывающий код считал, что
  // запрос не удался, хотя сервер ответил успехом.
  const text = await response.text();
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Идентификатор гостя переживает истечение токенов: он единственная связь с
 * учётной записью, поэтому хранится отдельно от них.
 */
export function getGuestId(): string | null {
  try {
    return localStorage.getItem(GUEST_KEY);
  } catch {
    return null;
  }
}

function setGuestId(guestId: string | null): void {
  try {
    if (guestId) {
      localStorage.setItem(GUEST_KEY, guestId);
    } else {
      localStorage.removeItem(GUEST_KEY);
    }
  } catch {
    // Приватный режим: гость останется в пределах текущей вкладки.
  }
}

export const api = {
  async loginWithTelegram(initData: string): Promise<AuthResponseDto> {
    const data = await request<AuthResponseDto>('/auth/telegram', {
      method: 'POST',
      body: { initData },
      retry: false,
    });
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    return data;
  },

  async loginAsGuest(): Promise<AuthResponseDto> {
    const saved = getGuestId();

    // Сначала попытка вернуться в существующую учётную запись: иначе каждый
    // заход создавал бы нового пользователя и терял историю.
    if (saved) {
      try {
        const resumed = await request<AuthResponseDto>('/auth/guest/resume', {
          method: 'POST',
          body: { guestId: saved },
          retry: false,
        });
        setTokens({ accessToken: resumed.accessToken, refreshToken: resumed.refreshToken });
        return resumed;
      } catch (error) {
        /*
          Новая запись заводится только тогда, когда прежней и вправду нет:
          на это сервер отвечает отказом в доступе. Всё остальное —
          «слишком часто», недоступность, обрыв связи — временно, и заводить
          на этом месте нового пользователя значит отобрать у человека и
          историю, и сохранённые диалоги из-за минутной заминки.
        */
        if (!(error instanceof ApiError) || error.status !== 401) {
          throw error;
        }
        setGuestId(null);
      }
    }

    const created = await request<AuthResponseDto & { guestId: string }>('/auth/guest', {
      method: 'POST',
      retry: false,
    });
    setTokens({ accessToken: created.accessToken, refreshToken: created.refreshToken });
    setGuestId(created.guestId);
    return created;
  },

  me(): Promise<UserDto> {
    return request<UserDto>('/users/me');
  },

  activeChat(): Promise<ChatWithMessagesDto | null> {
    return request<ChatWithMessagesDto | null>('/chats/active');
  },

  reportIssue(text: string, chatId: string | null, context: IssueContext): Promise<{ id: string }> {
    return request<{ id: string }>('/issues', {
      method: 'POST',
      body: { text, ...(chatId ? { chatId } : {}), context },
    });
  },

  savedChats(): Promise<SavedChatDto[]> {
    return request<SavedChatDto[]>('/friends');
  },

  /**
   * Ждущее предложение дружбы по диалогу.
   *
   * Спрашивается при открытии: предложение живёт пятнадцать минут и переживает
   * и перезагрузку вкладки, и уход человека входить через Telegram.
   */
  pendingFriendRequest(chatId: string): Promise<FriendRequestDto | null> {
    return request<FriendRequestDto | null>(`/friends/pending/${chatId}`);
  },

  removeFriend(chatId: string): Promise<{ removed: true }> {
    return request<{ removed: true }>(`/friends/${chatId}`, { method: 'DELETE' });
  },

  /** Заводит заявку на вход через бота и возвращает ссылку для человека. */
  startWebLogin(): Promise<WebLoginTicketDto> {
    return request<WebLoginTicketDto>('/auth/web/start', { method: 'POST', retry: false });
  },

  /**
   * Спрашивает, нажат ли «Старт».
   *
   * Сессия сохраняется здесь же: дальше приложение работает так, будто человек
   * вошёл обычным путём.
   */
  async webLoginStatus(code: string): Promise<WebLoginStatusDto> {
    const data = await request<WebLoginStatusDto>(`/auth/web/status?code=${code}`, {
      retry: false,
    });

    if (data.status === 'ready') {
      setTokens({
        accessToken: data.session.accessToken,
        refreshToken: data.session.refreshToken,
      });
    }

    return data;
  },

  /** Напоминания бота о непрочитанном: включить или выключить. */
  setNotifications(enabled: boolean): Promise<void> {
    return request<void>('/notifications', { method: 'PATCH', body: { enabled } });
  },

  /** Сохранённый диалог целиком: история открывается из «Моих чатов». */
  chatById(chatId: string): Promise<ChatWithMessagesDto | null> {
    return request<ChatWithMessagesDto | null>(`/chats/${chatId}`);
  },

  chatHistory(
    chatId: string,
    params: { limit?: number; before?: string } = {},
  ): Promise<{ items: MessageDto[]; hasMore: boolean }> {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    if (params.before) query.set('before', params.before);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<{ items: MessageDto[]; hasMore: boolean }>(`/chats/${chatId}/messages${suffix}`);
  },

  uploadMedia(
    file: File | Blob,
    chatId: string,
    options: {
      kind?: MediaKind;
      durationSec?: number;
      waveform?: string;
      filename?: string;
    } = {},
  ): Promise<{ media: { id: string; url: string } }> {
    const form = new FormData();
    form.append('file', file, options.filename ?? 'file');
    form.append('chatId', chatId);
    if (options.kind) {
      form.append('kind', options.kind);
    }
    if (options.durationSec !== undefined) {
      form.append('durationSec', String(options.durationSec));
    }
    if (options.waveform) {
      form.append('waveform', options.waveform);
    }

    return fetch(`${API_URL}/media/upload`, {
      method: 'POST',
      headers: tokens?.accessToken ? { authorization: `Bearer ${tokens.accessToken}` } : {},
      body: form,
    }).then(async (response) => {
      if (!response.ok) {
        throw await parseError(response);
      }
      return response.json() as Promise<{ media: { id: string; url: string } }>;
    });
  },
};
